import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { AuditLogSection } from '@/components/admin/AuditLogPage/AuditLogSection';
import { RolesSection } from '@/components/admin/RolesPage/RolesSection';
import { UsersSection } from '@/components/admin/UsersPage/UsersSection';
import { PageTabs, type PageTabDefinition } from '@/components/common/PageTabs';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';

const USERS_TAB_ID = 'users';
const ROLES_TAB_ID = 'roles';
const AUDIT_LOG_TAB_ID = 'auditLog';

function resolveActiveTabId(
  availableTabs: PageTabDefinition[],
  selectedTabId: string | null
): string | null {
  if (selectedTabId !== null) {
    const isSelectedTabAvailable = availableTabs.some(function matchesSelectedTab(tab) {
      return tab.id === selectedTabId;
    });
    if (isSelectedTabAvailable === true) {
      return selectedTabId;
    }
  }
  const firstAvailableTab = availableTabs[0];
  if (firstAvailableTab === undefined) {
    return null;
  }
  return firstAvailableTab.id;
}

export function AccessControlPage() {
  const { t } = useTranslation('admin');
  const canViewUsers = useHasPlatformPermission('user.read_all');
  const canViewRoles = useHasPlatformPermission('role.read_all');
  const canViewAuditLog = useHasPlatformPermission('audit.read_all');
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);

  const availableTabs: PageTabDefinition[] = [];
  if (canViewUsers === true) {
    availableTabs.push({ id: USERS_TAB_ID, label: t('accessControl.tabs.users') });
  }
  if (canViewRoles === true) {
    availableTabs.push({ id: ROLES_TAB_ID, label: t('accessControl.tabs.roles') });
  }
  if (canViewAuditLog === true) {
    availableTabs.push({ id: AUDIT_LOG_TAB_ID, label: t('accessControl.tabs.auditLog') });
  }

  const activeTabId = resolveActiveTabId(availableTabs, selectedTabId);

  if (activeTabId === null) {
    return <Navigate to={APPLICATION_ROUTES.adminDashboard} replace />;
  }

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader title={t('accessControl.title')} subtitle={t('accessControl.subtitle')} />

      <PageTabs
        tabs={availableTabs}
        activeTabId={activeTabId}
        onSelect={setSelectedTabId}
        ariaLabel={t('accessControl.title')}
        idPrefix="access-control"
      >
        {activeTabId === USERS_TAB_ID ? <UsersSection /> : null}
        {activeTabId === ROLES_TAB_ID ? <RolesSection /> : null}
        {activeTabId === AUDIT_LOG_TAB_ID ? <AuditLogSection /> : null}
      </PageTabs>
    </div>
  );
}
